import { Download } from '@mui/icons-material';
import {
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import AxiosConfig from '../../../../../config/AxiosConfig';
import ActionOnCommandIcon from '../../../svg-icons/ActionOnCommandIcon';
import QuoteIcon from '../../../utils/common/QuoteIcon';
import { getCurrencySymbol } from '../ActionPane/Orders/Retractions/FeesCalculations';

const QuoteRecap = ({ quote, currency }) => {
  const users = useSelector((state) => state.static.users);
  const [isLoading, setIsLoading] = useState(false);

  // Styles pour les tableaux (repris de StepThree.jsx)
  const tableCellStyle = {
    fontWeight: 'bold',
    fontSize: '0.7em',
    padding: '8px 4px',
  };

  const compactCellStyle = {
    padding: '4px 8px',
    fontSize: '0.8rem',
  };

  const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:nth-of-type(odd)': {
      backgroundColor: theme.palette.action.hover,
    },
    '&:last-child td, &:last-child th': {
      border: 0,
    },
  }));

  if (!quote) {
    return null;
  }

  const getAuthorName = () => {
    const user = users?.find((user) => user.email === quote?.author?.email);
    return user ? `${user?.firstName} ${user?.lastName}` : quote?.author?.email;
  };

  // FIXME: file is broken
  const handleDownloadQuote = async (quoteId) => {
    setIsLoading(quoteId);
    try {
      const response = await AxiosConfig.create(
        'get',
        `/quote/${quoteId}/download/`
      )
        .call()
        .getResponseData();

      const { data, contentType } = response;
      if (data && data?.length > 0) {
        const byteCharacters = atob(data);
        const byteNumbers = new Array(byteCharacters.length);

        // Convert to byte array
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);

        // Create blob and download link
        const blob = new Blob([byteArray], { type: contentType });
        const blobUrl = URL.createObjectURL(blob);

        // Download the file
        const link = document.createElement('a');
        link.setAttribute('download', `Devis-${quote?.reference}.pdf`);
        link.href = blobUrl;
        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);
        URL.revokeObjectURL(blobUrl);
      }
    } catch (error) {
      console.error('Error downloading quote:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const nameToDisplay = getAuthorName();

  return (
    <Paper
      elevation={16}
      key={quote?.reference}
      sx={{
        padding: 0,
        marginBottom: 1,
        boxShadow: 'none',
      }}
    >
      <Card
        elevation={5}
        sx={{
          display: 'flex',
          gap: 2,
          padding: 1,
          alignItems: 'center',
          justifyContent: 'space-evenly',
          flexDirection: 'column',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <ActionOnCommandIcon fontSize="large" />
            <Typography component={'span'} sx={{ fontSize: '0.9em' }}>
              Devis
            </Typography>
            <Chip label={quote?.store?.name} color="info" variant="outlined" />
            <Typography component={'span'} sx={{ fontSize: '0.9em' }}>
              {nameToDisplay}
            </Typography>
            <Typography component={'span'} variant="caption">
              {new Date(quote?.createdAt).toLocaleString('fr-FR', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: 'numeric',
                minute: 'numeric',
              })}
            </Typography>
          </Box>
          <Divider sx={{ mt: 1 }} />
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
            <Typography sx={{ fontSize: '0.9em' }}>
              <strong>Détails du devis :</strong>
            </Typography>

            {/* Nouveau tableau structuré inspiré de StepThree.jsx */}
            {quote?.lines && quote?.lines.length > 0 ? (
              <>
                <TableContainer component={Paper} sx={{ mt: 1, width: '100%' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell
                          sx={{ ...tableCellStyle, padding: '8px 4px' }}
                          align="left"
                        >
                          Référence - Titre
                        </TableCell>
                        <TableCell
                          sx={{ ...tableCellStyle, padding: '8px 4px' }}
                          align="center"
                        >
                          Quantité
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {quote.lines.map((line, index) => (
                        <StyledTableRow key={`${line?.reference}-${index}`}>
                          <TableCell sx={compactCellStyle}>
                            <strong>{line?.reference}</strong> -{' '}
                            {line?.title || 'N/A'}
                          </TableCell>
                          <TableCell sx={compactCellStyle} align="center">
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 'medium' }}
                            >
                              {line?.quantity}
                            </Typography>
                          </TableCell>
                        </StyledTableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Section Totaux inspirée de StepThree.jsx */}
                <Box
                  sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}
                >
                  <TableContainer component={Paper} sx={{ width: '300px' }}>
                    <Table size="small">
                      <TableBody>
                        <TableRow>
                          <TableCell
                            align="left"
                            sx={{ fontWeight: 'bold', fontSize: '0.8em' }}
                          >
                            Total HT
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{ fontWeight: 'bold', fontSize: '0.8em' }}
                          >
                            {quote?.amounts?.priceHt}{' '}
                            {getCurrencySymbol(currency)}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            align="left"
                            sx={{ fontWeight: 'bold', fontSize: '0.8em' }}
                          >
                            Total TVA ({quote?.amounts?.tvaValue}%)
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{ fontWeight: 'bold', fontSize: '0.8em' }}
                          >
                            {quote?.amounts?.totalVat}{' '}
                            {getCurrencySymbol(currency)}
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#e3f2fd' }}>
                          <TableCell
                            align="left"
                            sx={{
                              fontWeight: 'bold',
                              fontSize: '0.9em',
                              color: 'primary.main',
                            }}
                          >
                            Total TTC
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{
                              fontWeight: 'bold',
                              fontSize: '0.9em',
                              color: 'primary.main',
                            }}
                          >
                            {quote?.amounts?.priceTtc}{' '}
                            {getCurrencySymbol(currency)}
                          </TableCell>
                        </TableRow>
                        {quote?.discount > 0 && (
                          <TableRow>
                            <TableCell
                              align="left"
                              sx={{
                                fontWeight: 'bold',
                                fontSize: '0.8em',
                              }}
                            >
                              Remise
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{ fontWeight: 'bold', fontSize: '0.8em' }}
                            >
                              -{quote?.discount}%
                            </TableCell>
                          </TableRow>
                        )}
                        {quote?.amounts?.priceTtcNet && (
                          <TableRow>
                            <TableCell
                              align="left"
                              sx={{
                                fontWeight: 'bold',
                                fontSize: '0.8em',
                              }}
                            >
                              Total TTC Net
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{ fontWeight: 'bold', fontSize: '0.8em' }}
                            >
                              {quote?.amounts?.priceTtcNet}{' '}
                              {getCurrencySymbol(currency)}
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              </>
            ) : (
              <Paper sx={{ p: 2, textAlign: 'center', mt: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Aucun produit dans ce devis
                </Typography>
              </Paper>
            )}
          </Box>
          {quote?.boUuid && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                flexDirection: 'column',
                gap: 1,
                mt: 2,
              }}
            >
              <Button
                startIcon={
                  isLoading ? <CircularProgress size={16} /> : <Download />
                }
                onClick={() => handleDownloadQuote(quote?._id)}
                disabled={isLoading}
                size="small"
                variant="outlined"
                sx={{ width: 'fit-content', borderRadius: '5px' }}
              >
                Télécharger le devis
              </Button>
            </Box>
          )}
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            width: '100%',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <QuoteIcon boxSize={26} />
            <Chip
              label={`${quote?.status?.code === 'DRAFT' ? 'Brouillon de devis' : 'Devis créé'}`}
              color={
                quote?.status?.code === 'VALIDATED' ? 'success' : 'warning'
              }
              variant="outlined"
              size="small"
              sx={{ width: 'fit-content', borderRadius: '5px' }}
            />
          </Box>
          <Typography
            sx={{
              color: 'primary.main',
              fontWeight: 'bold',
            }}
          >
            {quote?.reference}
          </Typography>
        </Box>
      </Card>
    </Paper>
  );
};

export default QuoteRecap;
